/**
 * CookUp Core: patch schema for the Rust wavetable synth engine.
 *
 * `layer.core` is the editable, JSON-friendly description of one Core layer.
 * `coreParamBlock` flattens it into the Float32 parameter block the WASM
 * engine reads. The indices below mirror audio-dsp/src/synth/params.rs
 * exactly; change both together.
 */

export const CORE_TABLES = ['Basic Shapes', 'Pulse Width', 'Harmonic Sweep', 'Sync Sweep', 'Vocal Formant'];
export const CORE_FILTER_MODES = ['lp12', 'lp24', 'bp12', 'hp12', 'hp24', 'notch', 'peak', 'ladder24', 'ladder12', 'comb+', 'comb-', 'formant'];
export const CORE_FILTER_LABELS = {
  lp12: 'Low-pass 12', lp24: 'Low-pass 24', bp12: 'Band-pass', hp12: 'High-pass 12',
  hp24: 'High-pass 24', notch: 'Notch', peak: 'Peak',
  ladder24: 'Ladder 24 (Moog)', ladder12: 'Ladder 12', 'comb+': 'Comb +', 'comb-': 'Comb −', formant: 'Formant (vowel)'
};
export const CORE_FILTER_ROUTINGS = ['serial', 'parallel'];
export const CORE_VOICE_MODES = ['poly', 'mono', 'legato'];
export const CORE_GLIDE_MODES = ['always', 'legato'];
export const CORE_SUB_SHAPES = ['sine', 'triangle', 'square'];
/** Selecting this table index means "this oscillator's imported table". */
export const CORE_CUSTOM_TABLE = CORE_TABLES.length;
export const CORE_WARP_MODES = ['none', 'sync', 'bend+', 'bend-', 'squeeze', 'mirror', 'fm', 'ring', 'am'];
export const CORE_WARP_LABELS = {
  none: 'No warp', sync: 'Sync', 'bend+': 'Bend +', 'bend-': 'Bend −', squeeze: 'Squeeze (PWM)',
  mirror: 'Mirror', fm: 'FM from B', ring: 'Ring mod from B', am: 'AM from B'
};
/** Modes that read oscillator B, so only oscillator A offers them. */
export const CORE_CROSS_WARPS = ['fm', 'ring', 'am'];
export const CORE_QUALITIES = ['eco', 'standard', 'high'];

export const CORE_LFO_COUNT = 4;
export const CORE_MATRIX_SLOTS = 16;
export const CORE_LFO_SHAPES = ['sine', 'triangle', 'saw-up', 'saw-down', 'square', 'sample-hold', 'smooth-random'];
export const CORE_LFO_SHAPE_LABELS = {
  sine: 'Sine', triangle: 'Triangle', 'saw-up': 'Saw up', 'saw-down': 'Saw down', square: 'Square',
  'sample-hold': 'Sample & hold', 'smooth-random': 'Smooth random'
};
export const CORE_LFO_MODES = ['retrigger', 'free', 'one-shot'];
/** Tempo divisions in beats (quarter notes). */
export const CORE_LFO_DIVISIONS = [
  { label: '4 bars', beats: 16 }, { label: '2 bars', beats: 8 }, { label: '1 bar', beats: 4 },
  { label: '1/2', beats: 2 }, { label: '1/4', beats: 1 }, { label: '1/8', beats: 0.5 },
  { label: '1/8 T', beats: 1 / 3 }, { label: '1/16', beats: 0.25 }, { label: '1/16 T', beats: 1 / 6 },
  { label: '1/32', beats: 0.125 }
];
/** Matrix sources, in engine order. */
export const CORE_MOD_SOURCES = [
  'none', 'lfo1', 'lfo2', 'lfo3', 'lfo4', 'modEnv', 'ampEnv', 'velocity', 'key', 'modWheel',
  'aftertouch', 'random', 'macro1', 'macro2', 'macro3', 'macro4', 'pitchBend', 'timbre'
];
export const CORE_MOD_SOURCE_LABELS = {
  none: '—', lfo1: 'LFO 1', lfo2: 'LFO 2', lfo3: 'LFO 3', lfo4: 'LFO 4', modEnv: 'Mod env', ampEnv: 'Amp env',
  velocity: 'Velocity', key: 'Key', modWheel: 'Mod wheel', aftertouch: 'Pressure (AT / MPE Z)', random: 'Note random',
  macro1: 'Macro 1', macro2: 'Macro 2', macro3: 'Macro 3', macro4: 'Macro 4', pitchBend: 'Pitch bend', timbre: 'MPE timbre (Y)'
};
/** Matrix destinations, in engine order. */
export const CORE_MOD_DESTINATIONS = [
  'none', 'oscA.pitch', 'oscA.position', 'oscA.level', 'oscA.pan', 'oscA.warp', 'oscA.detune',
  'oscB.pitch', 'oscB.position', 'oscB.level', 'oscB.pan', 'oscB.warp', 'oscB.detune',
  'sub.level', 'noise.level', 'filter.cutoff', 'filter.resonance', 'filter.drive',
  'filter2.cutoff', 'filter2.resonance', 'amp', 'pitch',
  'fx.hyper.mix', 'fx.distortion.drive', 'fx.distortion.mix', 'fx.flanger.mix', 'fx.phaser.frequency',
  'fx.phaser.mix', 'fx.chorus.mix', 'fx.delay.mix', 'fx.delay.feedback', 'fx.multiband.depth',
  'fx.filter.cutoff', 'fx.filter.resonance', 'fx.reverb.mix'
];
export const CORE_MOD_DESTINATION_LABELS = {
  none: '—', 'oscA.pitch': 'A pitch', 'oscA.position': 'A position', 'oscA.level': 'A level', 'oscA.pan': 'A pan',
  'oscA.warp': 'A warp', 'oscA.detune': 'A detune', 'oscB.pitch': 'B pitch', 'oscB.position': 'B position',
  'oscB.level': 'B level', 'oscB.pan': 'B pan', 'oscB.warp': 'B warp', 'oscB.detune': 'B detune',
  'sub.level': 'Sub level', 'noise.level': 'Noise level', 'filter.cutoff': 'Filter 1 cutoff',
  'filter.resonance': 'Filter 1 resonance', 'filter.drive': 'Filter 1 drive', 'filter2.cutoff': 'Filter 2 cutoff',
  'filter2.resonance': 'Filter 2 resonance', amp: 'Amp', pitch: 'Pitch (all)',
  'fx.hyper.mix': 'FX Hyper mix', 'fx.distortion.drive': 'FX Distortion drive', 'fx.distortion.mix': 'FX Distortion mix',
  'fx.flanger.mix': 'FX Flanger mix', 'fx.phaser.frequency': 'FX Phaser frequency', 'fx.phaser.mix': 'FX Phaser mix',
  'fx.chorus.mix': 'FX Chorus mix', 'fx.delay.mix': 'FX Delay mix', 'fx.delay.feedback': 'FX Delay feedback',
  'fx.multiband.depth': 'FX Multiband depth', 'fx.filter.cutoff': 'FX Filter cutoff',
  'fx.filter.resonance': 'FX Filter resonance', 'fx.reverb.mix': 'FX Reverb mix'
};

/*
 * The effects rack, in the engine's module order. Each field's position is
 * its slot in the module's parameter block (slot 0 is on/off). `options`
 * fields store the option value; the engine receives its index. Defaults
 * must equal FX_DEFAULTS in audio-dsp/src/synth/params.rs.
 */
const knob = (key, label, min, max, value, extra = {}) => ({ key, label, min, max, default: value, ...extra });
const choice = (key, label, options, value) => ({ key, label, options, default: value });
const toggle = (key, label) => ({ key, label, toggle: true, default: false });
const division = (key, value) => ({ key, label: 'Division', division: true, min: 1 / 64, max: 64, default: value });
const MIX = (value) => knob('mix', 'Mix', 0, 1, value);

export const CORE_FX = [
  { key: 'hyper', label: 'Hyper / Dimension', fields: [
    knob('rate', 'Rate', 0.01, 10, 0.6, { unit: 'Hz' }), knob('detune', 'Detune', 0, 1, 0.35),
    knob('voices', 'Voices', 2, 6, 4, { step: 1 }), MIX(0.5), knob('size', 'Dim size', 0, 1, 0.3),
    knob('dimension', 'Dim mix', 0, 1, 0)
  ] },
  { key: 'distortion', label: 'Distortion', fields: [
    choice('mode', 'Mode', [
      { value: 'tube', label: 'Tube' }, { value: 'soft', label: 'Soft clip' }, { value: 'hard', label: 'Hard clip' },
      { value: 'diode', label: 'Diode' }, { value: 'fold', label: 'Linear fold' }, { value: 'sine-fold', label: 'Sine fold' },
      { value: 'asymmetric', label: 'Asymmetric' }, { value: 'bitcrush', label: 'Bitcrush' }, { value: 'downsample', label: 'Downsample' }
    ], 'tube'),
    knob('drive', 'Drive', 0, 1, 0.4), knob('tone', 'Tone', 200, 20000, 20000, { unit: 'Hz', log: true }),
    MIX(1), knob('output', 'Output', 0, 2, 1)
  ] },
  { key: 'flanger', label: 'Flanger', fields: [
    knob('rate', 'Rate', 0.01, 20, 0.25, { unit: 'Hz' }), knob('depth', 'Depth', 0, 1, 0.6),
    knob('feedback', 'Feedback', -0.95, 0.95, 0.5), MIX(0.5), toggle('sync', 'Sync'), division('division', 4),
    knob('stereo', 'Stereo', 0, 1, 0.25)
  ] },
  { key: 'phaser', label: 'Phaser', fields: [
    knob('rate', 'Rate', 0.01, 20, 0.4, { unit: 'Hz' }), knob('depth', 'Depth', 0, 1, 0.6),
    knob('frequency', 'Frequency', 20, 18000, 800, { unit: 'Hz', log: true }), knob('feedback', 'Feedback', -0.95, 0.95, 0.4),
    knob('stages', 'Stages', 2, 12, 6, { step: 2 }), MIX(0.5), toggle('sync', 'Sync'), division('division', 2),
    knob('stereo', 'Stereo', 0, 1, 0.25)
  ] },
  { key: 'chorus', label: 'Chorus', fields: [
    knob('rate', 'Rate', 0.01, 20, 0.8, { unit: 'Hz' }), knob('depth', 'Depth', 0, 1, 0.4),
    knob('delay', 'Delay', 1, 40, 12, { unit: 'ms' }), knob('feedback', 'Feedback', -0.9, 0.9, 0), MIX(0.5),
    knob('lowCut', 'Low cut', 20, 2000, 150, { unit: 'Hz', log: true }), toggle('sync', 'Sync'), division('division', 1)
  ] },
  { key: 'delay', label: 'Delay', fields: [
    knob('timeLeft', 'Time L', 1, 4000, 375, { unit: 'ms', log: true }), knob('timeRight', 'Time R', 1, 4000, 375, { unit: 'ms', log: true }),
    toggle('sync', 'Sync'), division('division', 0.75), knob('feedback', 'Feedback', 0, 0.98, 0.4),
    choice('mode', 'Mode', [{ value: 'stereo', label: 'Stereo' }, { value: 'dual', label: 'Dual L/R' }, { value: 'ping-pong', label: 'Ping-pong' }], 'ping-pong'),
    choice('style', 'Style', [
      { value: 'digital', label: 'Digital' }, { value: 'tape', label: 'Tape' }, { value: 'analog', label: 'Analog BBD' },
      { value: 'lofi', label: 'Lo-fi' }, { value: 'telephone', label: 'Telephone' }, { value: 'fuzz', label: 'Fuzz' }
    ], 'digital'),
    MIX(0.25), knob('highCut', 'High cut', 500, 20000, 12000, { unit: 'Hz', log: true })
  ] },
  { key: 'compressor', label: 'Compressor', fields: [
    knob('threshold', 'Threshold', -60, 0, -18, { unit: 'dB' }), knob('ratio', 'Ratio', 1, 30, 4),
    knob('attack', 'Attack', 0.05, 200, 10, { unit: 'ms', log: true }), knob('release', 'Release', 5, 2000, 120, { unit: 'ms', log: true }),
    knob('makeup', 'Makeup', -12, 30, 0, { unit: 'dB' }), MIX(1)
  ] },
  { key: 'multiband', label: 'Multiband (OTT)', fields: [
    knob('depth', 'Depth', 0, 1, 1), knob('time', 'Time', 0.05, 2, 1), knob('upward', 'Upward', 0, 1, 1),
    knob('downward', 'Downward', 0, 1, 1), knob('low', 'Low', -24, 24, 0, { unit: 'dB' }), knob('mid', 'Mid', -24, 24, 0, { unit: 'dB' }),
    knob('high', 'High', -24, 24, 0, { unit: 'dB' }), knob('input', 'In', -24, 24, 0, { unit: 'dB' }), knob('output', 'Out', -24, 24, 0, { unit: 'dB' })
  ] },
  { key: 'eq', label: 'EQ', fields: [
    knob('lowFrequency', 'Low', 20, 20000, 120, { unit: 'Hz', log: true }), knob('lowGain', 'Low gain', -24, 24, 0, { unit: 'dB' }),
    knob('midFrequency', 'Mid', 20, 20000, 1000, { unit: 'Hz', log: true }), knob('midGain', 'Mid gain', -24, 24, 0, { unit: 'dB' }),
    knob('midQ', 'Mid Q', 0.1, 18, 0.7, { log: true }), knob('highFrequency', 'High', 20, 20000, 8000, { unit: 'Hz', log: true }),
    knob('highGain', 'High gain', -24, 24, 0, { unit: 'dB' })
  ] },
  { key: 'filter', label: 'Filter', fields: [
    choice('mode', 'Mode', CORE_FILTER_MODES.map((mode) => ({ value: mode, label: CORE_FILTER_LABELS[mode] })), 'lp12'),
    knob('cutoff', 'Cutoff', 16, 20000, 2000, { unit: 'Hz', log: true }), knob('resonance', 'Resonance', 0, 1, 0.2),
    knob('drive', 'Drive', 0, 1, 0), MIX(1)
  ] },
  { key: 'reverb', label: 'Reverb', fields: [
    knob('size', 'Size', 0.25, 3, 1), knob('decay', 'Decay', 0.1, 30, 2.4, { unit: 's', log: true }),
    knob('preDelay', 'Pre-delay', 0, 250, 20, { unit: 'ms' }), knob('damping', 'Damping', 400, 20000, 6800, { unit: 'Hz', log: true }),
    knob('width', 'Width', 0, 1, 1), MIX(0.3), knob('modulation', 'Modulation', 0, 1, 0.45), knob('diffusion', 'Diffusion', 0, 1, 0.75)
  ] }
];
export const CORE_FX_KEYS = CORE_FX.map((module) => module.key);

export const defaultCoreFx = () => ({
  order: [...CORE_FX_KEYS],
  ...Object.fromEntries(CORE_FX.map((module) => [module.key, {
    enabled: false,
    ...Object.fromEntries(module.fields.map((field) => [field.key, field.default]))
  }]))
});

const normalizeFxField = (field, value) => {
  if (field.toggle) return value === true;
  if (field.options) return field.options.some((option) => option.value === value) ? value : field.default;
  const number = n(value, field.min, field.max, field.default);
  return field.step ? Math.round(number / field.step) * field.step : number;
};

export function normalizeCoreFx(candidate = {}) {
  const order = Array.isArray(candidate?.order) ? candidate.order.filter((key) => CORE_FX_KEYS.includes(key)) : [];
  const unique = [...new Set(order)];
  return {
    order: [...unique, ...CORE_FX_KEYS.filter((key) => !unique.includes(key))],
    ...Object.fromEntries(CORE_FX.map((module) => {
      const source = candidate?.[module.key] || {};
      return [module.key, {
        enabled: source.enabled === true,
        ...Object.fromEntries(module.fields.map((field) => [field.key, normalizeFxField(field, source[field.key])]))
      }];
    }))
  };
}
const QUALITY_OVERSAMPLING = { eco: 1, standard: 2, high: 4 };

export const CORE_PARAM_COUNT = 309;
export const CORE_PARAMS = Object.freeze({
  MASTER_LEVEL: 0, VOICE_MODE: 1, MAX_VOICES: 2, GLIDE_TIME: 3, GLIDE_MODE: 4,
  BEND_RANGE: 5, VELOCITY_TO_AMP: 6, MODWHEEL_TO_CUTOFF: 7,
  OSC_A: 8, OSC_B: 23,
  SUB_LEVEL: 38, SUB_OCTAVE: 39, SUB_SHAPE: 40, SUB_PAN: 41,
  NOISE_LEVEL: 42, NOISE_COLOR: 43, NOISE_PAN: 44,
  FILTER_ENABLED: 45, FILTER_MODE: 46, FILTER_CUTOFF: 47, FILTER_RESONANCE: 48,
  FILTER_DRIVE: 49, FILTER_KEYTRACK: 50, FILTER_ENV2: 51, FILTER_VELOCITY: 52,
  AMP_ENV: 53, MOD_ENV: 62,
  WARP_A: 72, WARP_B: 75, OVERSAMPLING: 78,
  FILTER2_ENABLED: 79, FILTER2_MODE: 80, FILTER2_CUTOFF: 81, FILTER2_RESONANCE: 82,
  FILTER2_DRIVE: 83, FILTER2_KEYTRACK: 84, FILTER2_ENV2: 85, FILTER_ROUTING: 86,
  TEMPO: 87, MACRO_BASE: 88, LFO_BASE: 92, LFO_BLOCK: 8, MATRIX_BASE: 124, MATRIX_BLOCK: 4,
  FX_BASE: 188, FX_BLOCK: 10, FX_ORDER: 298
});
const P = CORE_PARAMS;
const OSC = {
  ENABLED: 0, TABLE: 1, POSITION: 2, LEVEL: 3, PAN: 4, OCTAVE: 5, SEMI: 6, FINE: 7,
  UNISON: 8, DETUNE: 9, BLEND: 10, WIDTH: 11, RANDOM_PHASE: 12, PHASE: 13, ENV2_TO_POSITION: 14
};
const ENV = {
  DELAY: 0, ATTACK: 1, HOLD: 2, DECAY: 3, SUSTAIN: 4, RELEASE: 5,
  ATTACK_CURVE: 6, DECAY_CURVE: 7, RELEASE_CURVE: 8
};

export const CORE_EVENT = { NOTE_ON: 0, NOTE_OFF: 1, PITCH_BEND: 2, MOD_WHEEL: 3, ALL_NOTES_OFF: 4, AFTERTOUCH: 5, NOTE_PITCH: 6, NOTE_PRESSURE: 7, NOTE_TIMBRE: 8 };

const n = (value, min, max, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};
const oneOf = (value, options, fallback) => (options.includes(value) ? value : fallback);

const defaultOscillator = (enabled) => ({
  enabled,
  table: 0,
  position: 2 / 3,
  level: 0.75,
  pan: 0,
  octave: 0,
  semi: 0,
  fine: 0,
  unison: 1,
  detune: 20,
  blend: 0.75,
  width: 0.8,
  randomPhase: 1,
  phase: 0,
  envToPosition: 0,
  warp: { mode: 'none', amount: 0, envAmount: 0 },
  customTable: null
});

export const defaultLfo = () => ({
  shape: 'sine', rate: 2, sync: false, division: 1, mode: 'retrigger', phase: 0, delay: 0, rise: 0
});
export const defaultSlot = () => ({ source: 'none', destination: 'none', amount: 0, via: 'none' });

const defaultEnvelope = (overrides) => ({
  delay: 0, attack: 0.003, hold: 0, decay: 0.4, sustain: 0.8, release: 0.25,
  attackCurve: 0.3, decayCurve: 0.5, releaseCurve: 0.5, ...overrides
});

export const defaultCorePatch = () => ({
  voice: {
    mode: 'poly', voices: 16, glide: 0, glideMode: 'always',
    velocityToAmp: 0.7, modWheelToCutoff: 0, level: 0.7, quality: 'standard'
  },
  oscA: defaultOscillator(true),
  oscB: defaultOscillator(false),
  sub: { level: 0, octave: -1, shape: 'sine', pan: 0 },
  noise: { level: 0, color: 0.3, pan: 0 },
  filter: {
    enabled: true, mode: 'lp24', cutoff: 12000, resonance: 0.1, drive: 0,
    keytrack: 0, envAmount: 0, velocityAmount: 0
  },
  filter2: {
    enabled: false, mode: 'hp12', cutoff: 80, resonance: 0.1, drive: 0, keytrack: 0, envAmount: 0, routing: 'serial'
  },
  ampEnv: defaultEnvelope({}),
  modEnv: defaultEnvelope({ decay: 0.6, sustain: 0, release: 0.3 }),
  macros: [0, 0, 0, 0],
  macroBindings: [],
  lfos: Array.from({ length: CORE_LFO_COUNT }, () => defaultLfo()),
  matrix: Array.from({ length: CORE_MATRIX_SLOTS }, () => defaultSlot()),
  fx: defaultCoreFx()
});

const normalizeCustomTable = (candidate) => {
  if (!candidate || typeof candidate !== 'object' || !candidate.key) return null;
  return {
    key: String(candidate.key).slice(0, 120),
    name: String(candidate.name || 'Imported table').slice(0, 60),
    frames: Math.round(n(candidate.frames, 1, 256, 1)),
    storageKey: candidate.storageKey ? String(candidate.storageKey).slice(0, 1000) : ''
  };
};

const normalizeOscillator = (candidate, fallback, { allowCross = true } = {}) => {
  const customTable = normalizeCustomTable(candidate?.customTable);
  const table = Math.round(n(candidate?.table, 0, CORE_CUSTOM_TABLE, fallback.table));
  const warpMode = oneOf(candidate?.warp?.mode, CORE_WARP_MODES, 'none');
  return {
  enabled: candidate?.enabled == null ? fallback.enabled : candidate.enabled === true,
  // The custom slot only means something once a table has been imported.
  table: table === CORE_CUSTOM_TABLE && !customTable ? 0 : table,
  customTable,
  warp: {
    mode: !allowCross && CORE_CROSS_WARPS.includes(warpMode) ? 'none' : warpMode,
    amount: n(candidate?.warp?.amount, 0, 1, 0),
    envAmount: n(candidate?.warp?.envAmount, -1, 1, 0)
  },
  position: n(candidate?.position, 0, 1, fallback.position),
  level: n(candidate?.level, 0, 1, fallback.level),
  pan: n(candidate?.pan, -1, 1, fallback.pan),
  octave: Math.round(n(candidate?.octave, -4, 4, fallback.octave)),
  semi: Math.round(n(candidate?.semi, -12, 12, fallback.semi)),
  fine: n(candidate?.fine, -100, 100, fallback.fine),
  unison: Math.round(n(candidate?.unison, 1, 16, fallback.unison)),
  detune: n(candidate?.detune, 0, 100, fallback.detune),
  blend: n(candidate?.blend, 0, 1, fallback.blend),
  width: n(candidate?.width, 0, 1, fallback.width),
  randomPhase: n(candidate?.randomPhase, 0, 1, fallback.randomPhase),
  phase: n(candidate?.phase, 0, 1, fallback.phase),
  envToPosition: n(candidate?.envToPosition, -1, 1, fallback.envToPosition)
  };
};

const normalizeEnvelope = (candidate, fallback) => ({
  delay: n(candidate?.delay, 0, 10, fallback.delay),
  attack: n(candidate?.attack, 0, 30, fallback.attack),
  hold: n(candidate?.hold, 0, 10, fallback.hold),
  decay: n(candidate?.decay, 0, 30, fallback.decay),
  sustain: n(candidate?.sustain, 0, 1, fallback.sustain),
  release: n(candidate?.release, 0, 30, fallback.release),
  attackCurve: n(candidate?.attackCurve, -1, 1, fallback.attackCurve),
  decayCurve: n(candidate?.decayCurve, -1, 1, fallback.decayCurve),
  releaseCurve: n(candidate?.releaseCurve, -1, 1, fallback.releaseCurve)
});

export function normalizeCorePatch(candidate = {}) {
  const base = defaultCorePatch();
  const voice = candidate?.voice || {};
  const filter = candidate?.filter || {};
  return {
    voice: {
      mode: oneOf(voice.mode, CORE_VOICE_MODES, base.voice.mode),
      voices: Math.round(n(voice.voices, 1, 32, base.voice.voices)),
      glide: n(voice.glide, 0, 10, base.voice.glide),
      glideMode: oneOf(voice.glideMode, CORE_GLIDE_MODES, base.voice.glideMode),
      velocityToAmp: n(voice.velocityToAmp, 0, 1, base.voice.velocityToAmp),
      modWheelToCutoff: n(voice.modWheelToCutoff, -8, 8, base.voice.modWheelToCutoff),
      level: n(voice.level, 0, 1, base.voice.level),
      quality: oneOf(voice.quality, CORE_QUALITIES, base.voice.quality)
    },
    oscA: normalizeOscillator(candidate?.oscA, base.oscA),
    oscB: normalizeOscillator(candidate?.oscB, base.oscB, { allowCross: false }),
    sub: {
      level: n(candidate?.sub?.level, 0, 1, base.sub.level),
      octave: Math.round(n(candidate?.sub?.octave, -2, -1, base.sub.octave)),
      shape: oneOf(candidate?.sub?.shape, CORE_SUB_SHAPES, base.sub.shape),
      pan: n(candidate?.sub?.pan, -1, 1, base.sub.pan)
    },
    noise: {
      level: n(candidate?.noise?.level, 0, 1, base.noise.level),
      color: n(candidate?.noise?.color, 0, 1, base.noise.color),
      pan: n(candidate?.noise?.pan, -1, 1, base.noise.pan)
    },
    filter: {
      enabled: filter.enabled == null ? base.filter.enabled : filter.enabled === true,
      mode: oneOf(filter.mode, CORE_FILTER_MODES, base.filter.mode),
      cutoff: n(filter.cutoff, 20, 20000, base.filter.cutoff),
      resonance: n(filter.resonance, 0, 1, base.filter.resonance),
      drive: n(filter.drive, 0, 1, base.filter.drive),
      keytrack: n(filter.keytrack, 0, 1, base.filter.keytrack),
      envAmount: n(filter.envAmount, -8, 8, base.filter.envAmount),
      velocityAmount: n(filter.velocityAmount, 0, 4, base.filter.velocityAmount)
    },
    filter2: {
      enabled: candidate?.filter2?.enabled === true,
      mode: oneOf(candidate?.filter2?.mode, CORE_FILTER_MODES, base.filter2.mode),
      cutoff: n(candidate?.filter2?.cutoff, 20, 20000, base.filter2.cutoff),
      resonance: n(candidate?.filter2?.resonance, 0, 1, base.filter2.resonance),
      drive: n(candidate?.filter2?.drive, 0, 1, base.filter2.drive),
      keytrack: n(candidate?.filter2?.keytrack, 0, 1, base.filter2.keytrack),
      envAmount: n(candidate?.filter2?.envAmount, -8, 8, base.filter2.envAmount),
      routing: oneOf(candidate?.filter2?.routing, CORE_FILTER_ROUTINGS, base.filter2.routing)
    },
    ampEnv: normalizeEnvelope(candidate?.ampEnv, base.ampEnv),
    modEnv: normalizeEnvelope(candidate?.modEnv, base.modEnv),
    macroBindings: Array.isArray(candidate?.macroBindings) ? candidate.macroBindings.slice(0,4).map(value => ['Punch','Metal','Dirt','Width','Motion','Air','Body','Space'].includes(value) ? value : null) : [],
    macros: Array.from({ length: 4 }, (_, index) => n(candidate?.macros?.[index], 0, 1, 0)),
    lfos: Array.from({ length: CORE_LFO_COUNT }, (_, index) => {
      const lfo = candidate?.lfos?.[index] || {};
      const fallback = defaultLfo();
      return {
        shape: oneOf(lfo.shape, CORE_LFO_SHAPES, fallback.shape),
        rate: n(lfo.rate, 0.01, 40, fallback.rate),
        sync: lfo.sync === true,
        division: n(lfo.division, 1 / 64, 64, fallback.division),
        mode: oneOf(lfo.mode, CORE_LFO_MODES, fallback.mode),
        phase: n(lfo.phase, 0, 1, fallback.phase),
        delay: n(lfo.delay, 0, 10, fallback.delay),
        rise: n(lfo.rise, 0, 10, fallback.rise)
      };
    }),
    matrix: Array.from({ length: CORE_MATRIX_SLOTS }, (_, index) => {
      const slot = candidate?.matrix?.[index] || {};
      return {
        source: oneOf(slot.source, CORE_MOD_SOURCES, 'none'),
        destination: oneOf(slot.destination, CORE_MOD_DESTINATIONS, 'none'),
        amount: n(slot.amount, -1, 1, 0),
        via: oneOf(slot.via, CORE_MOD_SOURCES, 'none')
      };
    }),
    fx: normalizeCoreFx(candidate?.fx)
  };
}

const writeOscillator = (block, base, warpBase, slot, osc) => {
  block[base + OSC.ENABLED] = osc.enabled ? 1 : 0;
  // Custom tables live in engine slots after the factory tables: A → slot 0, B → slot 1.
  block[base + OSC.TABLE] = osc.table === CORE_CUSTOM_TABLE ? CORE_CUSTOM_TABLE + slot : osc.table;
  block[warpBase] = CORE_WARP_MODES.indexOf(osc.warp.mode);
  block[warpBase + 1] = osc.warp.amount;
  block[warpBase + 2] = osc.warp.envAmount;
  block[base + OSC.POSITION] = osc.position;
  block[base + OSC.LEVEL] = osc.level;
  block[base + OSC.PAN] = osc.pan;
  block[base + OSC.OCTAVE] = osc.octave;
  block[base + OSC.SEMI] = osc.semi;
  block[base + OSC.FINE] = osc.fine;
  block[base + OSC.UNISON] = osc.unison;
  block[base + OSC.DETUNE] = osc.detune;
  block[base + OSC.BLEND] = osc.blend;
  block[base + OSC.WIDTH] = osc.width;
  block[base + OSC.RANDOM_PHASE] = osc.randomPhase;
  block[base + OSC.PHASE] = osc.phase;
  block[base + OSC.ENV2_TO_POSITION] = osc.envToPosition;
};

const writeEnvelope = (block, base, env) => {
  block[base + ENV.DELAY] = env.delay;
  block[base + ENV.ATTACK] = env.attack;
  block[base + ENV.HOLD] = env.hold;
  block[base + ENV.DECAY] = env.decay;
  block[base + ENV.SUSTAIN] = env.sustain;
  block[base + ENV.RELEASE] = env.release;
  block[base + ENV.ATTACK_CURVE] = env.attackCurve;
  block[base + ENV.DECAY_CURVE] = env.decayCurve;
  block[base + ENV.RELEASE_CURVE] = env.releaseCurve;
};

/**
 * Flatten a (normalized) Core patch into the engine's parameter block.
 * `bendRange` comes from the instrument's performance settings so every
 * layer bends together.
 */
export function coreParamBlock(core, { bendRange = 2, offline = false, tempo = 120, macroValues = {} } = {}) {
  const patch = normalizeCorePatch(core);
  patch.macroBindings.forEach((name,index) => { if (name && Number.isFinite(Number(macroValues[name]))) patch.macros[index] = n(macroValues[name],0,1,patch.macros[index]); });
  const block = new Array(CORE_PARAM_COUNT).fill(0);
  block[P.MASTER_LEVEL] = patch.voice.level;
  block[P.VOICE_MODE] = CORE_VOICE_MODES.indexOf(patch.voice.mode);
  block[P.MAX_VOICES] = patch.voice.voices;
  block[P.GLIDE_TIME] = patch.voice.glide;
  block[P.GLIDE_MODE] = CORE_GLIDE_MODES.indexOf(patch.voice.glideMode);
  block[P.BEND_RANGE] = n(bendRange, 0, 48, 2);
  block[P.VELOCITY_TO_AMP] = patch.voice.velocityToAmp;
  block[P.MODWHEEL_TO_CUTOFF] = patch.voice.modWheelToCutoff;
  writeOscillator(block, P.OSC_A, P.WARP_A, 0, patch.oscA);
  writeOscillator(block, P.OSC_B, P.WARP_B, 1, patch.oscB);
  block[P.FILTER2_ENABLED] = patch.filter2.enabled ? 1 : 0;
  block[P.FILTER2_MODE] = CORE_FILTER_MODES.indexOf(patch.filter2.mode);
  block[P.FILTER2_CUTOFF] = patch.filter2.cutoff;
  block[P.FILTER2_RESONANCE] = patch.filter2.resonance;
  block[P.FILTER2_DRIVE] = patch.filter2.drive;
  block[P.FILTER2_KEYTRACK] = patch.filter2.keytrack;
  block[P.FILTER2_ENV2] = patch.filter2.envAmount;
  block[P.FILTER_ROUTING] = CORE_FILTER_ROUTINGS.indexOf(patch.filter2.routing);
  block[P.TEMPO] = n(tempo, 20, 400, 120);
  patch.macros.forEach((value, index) => { block[P.MACRO_BASE + index] = value; });
  patch.lfos.forEach((lfo, index) => {
    const base = P.LFO_BASE + index * P.LFO_BLOCK;
    block[base] = CORE_LFO_SHAPES.indexOf(lfo.shape);
    block[base + 1] = lfo.rate;
    block[base + 2] = lfo.sync ? 1 : 0;
    block[base + 3] = lfo.division;
    block[base + 4] = CORE_LFO_MODES.indexOf(lfo.mode);
    block[base + 5] = lfo.phase;
    block[base + 6] = lfo.delay;
    block[base + 7] = lfo.rise;
  });
  patch.matrix.forEach((slot, index) => {
    const base = P.MATRIX_BASE + index * P.MATRIX_BLOCK;
    block[base] = CORE_MOD_SOURCES.indexOf(slot.source);
    block[base + 1] = CORE_MOD_DESTINATIONS.indexOf(slot.destination);
    block[base + 2] = slot.amount;
    block[base + 3] = CORE_MOD_SOURCES.indexOf(slot.via);
  });
  CORE_FX.forEach((module, moduleIndex) => {
    const base = P.FX_BASE + moduleIndex * P.FX_BLOCK;
    const settings = patch.fx[module.key];
    block[base] = settings.enabled ? 1 : 0;
    module.fields.forEach((field, fieldIndex) => {
      const value = settings[field.key];
      block[base + fieldIndex + 1] = field.toggle
        ? (value ? 1 : 0)
        : field.options ? field.options.findIndex((option) => option.value === value) : value;
    });
  });
  patch.fx.order.forEach((key, slot) => { block[P.FX_ORDER + slot] = CORE_FX_KEYS.indexOf(key); });
  // Exports render at the highest quality regardless of the live setting.
  block[P.OVERSAMPLING] = offline ? 4 : QUALITY_OVERSAMPLING[patch.voice.quality];
  block[P.SUB_LEVEL] = patch.sub.level;
  block[P.SUB_OCTAVE] = patch.sub.octave;
  block[P.SUB_SHAPE] = CORE_SUB_SHAPES.indexOf(patch.sub.shape);
  block[P.SUB_PAN] = patch.sub.pan;
  block[P.NOISE_LEVEL] = patch.noise.level;
  block[P.NOISE_COLOR] = patch.noise.color;
  block[P.NOISE_PAN] = patch.noise.pan;
  block[P.FILTER_ENABLED] = patch.filter.enabled ? 1 : 0;
  block[P.FILTER_MODE] = CORE_FILTER_MODES.indexOf(patch.filter.mode);
  block[P.FILTER_CUTOFF] = patch.filter.cutoff;
  block[P.FILTER_RESONANCE] = patch.filter.resonance;
  block[P.FILTER_DRIVE] = patch.filter.drive;
  block[P.FILTER_KEYTRACK] = patch.filter.keytrack;
  block[P.FILTER_ENV2] = patch.filter.envAmount;
  block[P.FILTER_VELOCITY] = patch.filter.velocityAmount;
  writeEnvelope(block, P.AMP_ENV, patch.ampEnv);
  writeEnvelope(block, P.MOD_ENV, patch.modEnv);
  return block;
}

/** Stable 32-bit id for a voice string, shared with the worklet. */
export function coreVoiceId(text) {
  let hash = 2166136261;
  const value = String(text || 'voice');
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
